import {
  getCountryColorList,
  getSingleLineColors,
  getThreeLineColors,
  getTwoLineColors,
  getTwoCountriesColors,
} from "../functions/colors";
import { formatNumber } from "../functions/formatNumber";
import { generateMultiplLineTooltip } from "../functions/generateLineTooltip";
import { makeSeries } from "../functions/makeSeries";

// Grid spacing configurations
export const GRID_CONFIG = {
  left: "82px",
  top: "5%",
  height: "90%",
  width: "82%",
};

// Grid spacing configurations when graph includes legend
export const GRID_CONFIG_WITH_LEGEND = {
  left: "50px",
  top: "15%",
  height: "76%",
  width: "82%",
};

// Config for Legends in graphs
export const LEGEND_CONFIG = {
  show: true,
  orient: "horizontal",
  top: "0%",
  left: "center",
  width: "90%",
  type: "scroll",
};

//Config to hide legends in echarts graphs
export const NO_LEGEND_CONFIG = {
  show: false,
};

// Config for small screen treemaps
export const SMALL_SCREEN_TREEMAP_CONFIG = {
  top: 0,
  right: 0,
  width: "100%",
  height: "93%",
  breadcrumb: {
    top: "93%",
    left: 0,
    height: "35",
    itemStyle: {
      color: "rgba(255, 255, 255, 0.5)",
      borderWidth: 2,
      textStyle: {
        fontSize: 14,
        color: "black",
      },
    },
  },
};

// Config for tooltip style
export const TOOLTIP_STYLE = {
  backgroundColor: "rgb(255,255,255)",
  borderColor: "white",
  textStyle: {
    fontSize: 14,
    fontFamily: "Open Sans",
  },
  padding: [12, 56, 12, 24],
  className: "round-corners text-align-left bold-span",
  confine: true,
};

/**
 *
 * @param {string} currentCountry Country code
 * @param {Array <string>} countries Array of line labels, usually countries
 * @param {string} colorRBG Color in hex format
 * @param {Array <string>} xAxisData Array of x Axis labels, usually dates
 * @param {string} yAxisLabelFormatter Formatter according to Echarts label formatter
 * @param {boolean} isNotSelectable Whether legend will be selectable
 * @param {string} language "es" or "en"
 * @returns Config object
 */
export const getSimpleLineConfig = (
  currentCountry,
  countries,
  colorRBG,
  xAxisData,
  isYAxisPercentage,
  isNotSelectable,
  language
) => {
  const config = {
    color:
      currentCountry === "LAC"
        ? getCountryColorList(countries, colorRBG)
        : getSingleLineColors(),
    backgroundColor: "transparent",
    xAxis: {
      type: "category",
      data: xAxisData,
      axisTick: {
        show: false,
      },
      //boundaryGap: true,
      axisLabel: {
        showMaxLabel: true,
        margin: 10,
        interval: 2
      },
    },
    yAxis: {
      type: "value",
      axisLine: {
        show: true,
      },
      axisLabel: {
        margin: 20,
        fontSize: 14,
        formatter: (value) => {
          return formatNumber(value, language) + (isYAxisPercentage ? "%" : "");
        },
      },
    },
    legend:
      currentCountry === "LAC"
        ? {
            ...LEGEND_CONFIG,
            data: countries,
            selectedMode: !isNotSelectable,
            selected: (function () {
              let noSelected = {};
              countries.forEach((e, i) => {
                if (i === 0) {
                  noSelected[e] = true;
                } else {
                  noSelected[e] = isNotSelectable;
                }
              });
              return noSelected;
            })(),
          }
        : NO_LEGEND_CONFIG,
  };
 

  return config;
};

/**
 *
 * @param {Object} response Response object from API
 * @param {Array <string>} xAxisData Array of x Axis labels, usually dates
 * @param {string} language Language ("es" or "en")
 * @returns
 */
export function getSentimentLineGraphOptions(response, xAxisData, language) {
  const options = {
    color: getThreeLineColors(),
    backgroundColor: "transparent",
    xAxis: {
      type: "category",
      data: xAxisData,
      axisTick: {
        show: false,
      },
      //boundaryGap: true,
      axisLabel: {
        showMaxLabel: true,
        margin: 10,
        interval: 2
      },
    },
    yAxis: {
      type: "value",
      axisLine: {
        show: true,
      },
      axisLabel: {
        margin: 20,
        fontSize: 14,
        formatter: "{value}%",
      },
    },
    tooltip: {
      trigger: "axis",
      ...TOOLTIP_STYLE,
      formatter: function (params) {
        const transformedNames =
          language === "en"
            ? {
                Positive: response.data.data.positive,
                Neutral: response.data.data.neutral,
                Xenophobic: response.data.data.xeno,
              }
            : {
                Positivo: response.data.data.positive,
                Neutral: response.data.data.neutral,
                Xenófobo: response.data.data.xeno,
              };
        const isPercentage = true;
        const areColorsSwitched = false;
        return generateMultiplLineTooltip(
          params,
          transformedNames,
          isPercentage,
          areColorsSwitched,
          language
        );
      },
    },
    legend: {
      ...LEGEND_CONFIG,
      data:
        language === "en"
          ? ["Positive", "Xenophobic", "Neutral"]
          : ["Positivo", "Xenófobo", "Neutral"],
    },
    grid: GRID_CONFIG_WITH_LEGEND,
    series: makeSeries(response.data, language),
  };
  return options;
}

/**
 *
 * @param {Array <string>} xAxisData Array of x Axis labels, usually dates
 * @param {string} yAxisLabelFormatter Formatter according to Echarts label formatter
 * @param {Array <string>} countries Array of line labels, usually countries
 * @param {string} colorRBG Color in hex format
 * @param {string} language "es" or "en"
 * @returns
 */
export function getTwoLineGraphOptions(
  xAxisData,
  yAxisLabelFormatter,
  countries,
  colorRBG,
  language
) {
  const options = {
    backgroundColor: "transparent",
    //update the xAxis to always show the last date
    xAxis: {
      type: "category",
      data: xAxisData,
      axisTick: {
        show: false,
      },
      axisLabel: {
        showMaxLabel: true,
        margin: 10,
        interval: 2
      },
    },
    yAxis: {
      type: "value",
      axisLine: {
        show: true,
      },
      axisLabel: {
        margin: 20,
        fontSize: 14,
        formatter: (value) => {
          return formatNumber(value, language);
        },
      },
    },
    grid: GRID_CONFIG_WITH_LEGEND,
  };
  let colors;
  if (countries === null) {
    colors = getTwoLineColors();
  } else {
    colors =
      countries.length === 2
        ? getTwoCountriesColors()
        : getCountryColorList(countries, colorRBG);
  }

  options.color = colors;

  if (yAxisLabelFormatter)
    options.yAxis.axisLabel.formatter = yAxisLabelFormatter;
  return options;
}
